import { useI18n } from 'i18n-utils'
import { ApiStatus, KycServices, UCenterServices } from 'network'
import { v1 } from 'uuid'
import { PageRoutes } from '../../../crypto-otc/src/constants/PageRoutes'
import { StorageKeys } from '../constants/static-table-sv-id'
import { useChangeLanguage } from '../hooks/use-change-language'
import { useAssetsStore } from './use-assets'
import { useStaticTableDataStore } from './use-static-table-data'

export type CurrentCurrency<T extends keyof ApiResponse.OtcCoinRate> = T

export enum KycStatus {
  /** 未认证 */
  NotKyc = 0,
  /** 审核中 */
  KycIng = 1,
  /** 重新认证 */
  KycReAuth = 2,
  /** 认证成功 */
  KycSuccess = 3,
}

export enum KycLevel {
  /** 低 */
  Low = 0,
  /** 中 */
  Middle = 1,
  /** 高 */
  High = 2,
}

export enum SafetyLevel {
  /** 低 */
  Low = 1,
  /** 中 */
  Middle = 2,
  /** 高 */
  High = 3,
}

const safetyLevelMap = {
  [SafetyLevel.Low]: '较低',
  [SafetyLevel.Middle]: '中等',
  [SafetyLevel.High]: '良好',
}

export const userDeviceId = window.localStorage.getItem(StorageKeys.DeviceId) || v1()
window.localStorage.setItem(StorageKeys.DeviceId, userDeviceId)

export const useUserInfoStore = defineStore('userInfo', () => {
  const token = ref<string>(window.localStorage.getItem(StorageKeys.Token) || '')
  const deviceId = ref<string>(userDeviceId)
  const currentFromCurrency = ref<CurrentCurrency<keyof ApiResponse.OtcCoinRate>>('usdt')
  const currentToCurrency = ref<CurrentCurrency<keyof ApiResponse.OtcCoinRate>>(
    (window.localStorage.getItem(StorageKeys.CurrentCurrency) as CurrentCurrency<keyof ApiResponse.OtcCoinRate>) || 'cny',
  )
  const staticTableDataStore = useStaticTableDataStore()
  const lastLoginInfo = ref<ApiResponse.UserLoginList>()
  const userInfo = ref<ApiResponse.OnlineUserInfo>()
  const loading = ref<boolean>(false)
  const router = useRouter()
  const userConfigs = ref<ApiResponse.UserConfigs>()
  const assetsStore = useAssetsStore()
  const { t } = useI18n()

  // KYC 相关状态
  const kycInfo = ref<Partial<ApiResponse.KycDetailInfo>>({
    level: 0,
    status: 0,
  })
  const kycLevels = ref<ApiResponse.ThirdKycInfo[]>([])
  const seniorThirdType = ref<number | undefined>()
  const isKycLoading = ref(false)

  const getDeviceId = () => {
    return deviceId.value
  }

  const getUserInfo = async () => {
    if (!isLogin.value) {
      return
    }
    fetchKycData()
    loading.value = true
    const res = await UCenterServices.getUserInfo()
    userInfo.value = res.data.userInfo
    loading.value = false
  }

  const setToken = (tokenValue: string) => {
    token.value = tokenValue
    window.localStorage.setItem(StorageKeys.Token, tokenValue)
  }

  const isLogin = computed(() => {
    return !!token.value
  })

  const logout = () => {
    token.value = ''
    userInfo.value = undefined
    kycLevels.value = []
    kycInfo.value = {
      status: 0,
      level: 0,
    }
    window.localStorage.removeItem(StorageKeys.Token)
    lastLoginInfo.value = undefined
    assetsStore.$dispose()
    router.push({
      path: PageRoutes.Login,
    })
  }

  const logoutAndToHome = () => {
    logout()
    router.push(PageRoutes.Home)
  }

  const setToCurrency = (currency: CurrentCurrency<keyof ApiResponse.OtcCoinRate>) => {
    currentToCurrency.value = currency
    window.localStorage.setItem(StorageKeys.CurrentCurrency, currency)
  }

  const currencyList = computed(() => {
    return staticTableDataStore.currencyList
  })

  const moneyMark = computed(() => {
    return currencyList.value.find((item) => item.logicName === currentToCurrency.value)?.mark || '￥'
  })

  const currentKycName = computed(() => {
    return kycLevels.value.find((item) => item.level === kycInfo.value.level)?.levelName || t('未认证')
  })

  const currentKycLevel = computed(() => {
    return kycInfo.value.level
  })

  // 当前用户vip等级
  const currentVipLevel = computed<number>(() => {
    return userInfo.value?.userInfo.btVipLevel || 0
  })

  const currentSafetyLevel = computed(() => {
    const isBindList = [isBindEmail.value, isBindGoogle.value, isBindMobile.value]
    const isTrueCount = isBindList.filter((item) => item).length
    if (isTrueCount === 1) {
      return SafetyLevel.Low
    } else if (isTrueCount === 2) {
      return SafetyLevel.Middle
    } else if (isTrueCount === 3) {
      return SafetyLevel.High
    }
    return SafetyLevel.Low
  })

  /** 现在WEB端暂时没有接入KYC等功能，所以通过是否绑定邮箱、谷歌、手机号来判断安全等级 */
  const currentSafetyLevelLabel = computed(() => {
    return safetyLevelMap[currentSafetyLevel.value]
  })

  const setLastLoginInfo = (info: ApiResponse.UserLoginList) => {
    lastLoginInfo.value = info
  }

  const isBindEmail = computed(() => {
    return userInfo.value?.authInfo.openEmail === 1
  })

  const isBindGoogle = computed(() => {
    return userInfo.value?.authInfo.openGoogle === 1
  })

  const isBindMobile = computed(() => {
    return userInfo.value?.authInfo.openMobile === 1
  })

  const isSetTradePassword = computed(() => {
    return userInfo.value?.authInfo.openTradePassword === 1
  })

  const isOpenPassKey = computed(() => {
    return userInfo.value?.authInfo.openPassKey === 1
  })

  const fetchUserConfigs = async () => {
    if (!isLogin.value) {
      return
    }
    const res = await UCenterServices.getUserConfigs()
    if (res.status !== ApiStatus.Success) return
    userConfigs.value = res.data
  }

  // 获取 KYC 数据
  const fetchKycData = async () => {
    if (!isLogin.value) {
      return
    }
    isKycLoading.value = true
    // 同时调用两个接口
    const [kycInfoRes, kycIntroV2Res] = await Promise.all([KycServices.getKycInfo(), KycServices.getKycIntroductionV2()])

    if (kycInfoRes.status === ApiStatus.Success) {
      const actualData = kycInfoRes.data
      kycInfo.value = actualData
    }
    if (kycIntroV2Res.status === ApiStatus.Success) {
      kycLevels.value = kycIntroV2Res.data
      // 提取高级身份认证（level==2）的 thirdType
      const seniorItem = (kycLevels.value || []).find((it) => it?.level === 2)
      seniorThirdType.value = seniorItem?.thirdType
    }
    isKycLoading.value = false
  }

  useChangeLanguage(fetchUserConfigs)

  useChangeLanguage(getUserInfo)

  useChangeLanguage(async () => {
    if (isLogin.value) {
      await assetsStore.fetchAssetItemList()
    }
  })

  /** 刷新用户信息 */
  const refreshUserInfo = async () => {
    await getUserInfo()
  }

  const isOpenSecurity = computed(() => {
    // 暂时忽略同行密钥，暂时还不懂这个需求
    return isBindEmail.value || isBindGoogle.value || isBindMobile.value
  })

  const clearToken = () => {
    token.value = ''
    window.localStorage.removeItem(StorageKeys.Token)
  }

  const getCoinPermissionByItemId = (itemId: ItemId): ApiResponse.UserConfigWithWithdrawSetting => {
    const permission = userConfigs.value?.withdrawSettings.find((item) => item.itemId === itemId)
    if (!permission)
      return {
        itemId,
        inStatus: -1,
        outStatus: -1,
        transferIn: -1,
        transferOut: -1,
        recharge: -1,
        withdraw: -1,
      }
    return permission
  }

  const setUserInfo = (info: ApiResponse.OnlineUserInfo | undefined) => {
    userInfo.value = info
  }

  const currentNickname = computed(() => {
    return userInfo.value?.userInfo.nickName || userInfo.value?.accountInfo.username
  })

  const currentArea = computed(() => {
    const area = userInfo.value?.accountInfo.area
    if (!area) return ''
    if (area.includes('+')) return area
    return `+${area}`
  })

  const getToken = () => {
    return window.localStorage.getItem(StorageKeys.Token) || ''
  }

  return {
    token,
    getDeviceId,
    currentFromCurrency,
    currentToCurrency,
    isLogin,
    setToken,
    logout,
    setToCurrency,
    moneyMark,
    userInfo,
    currentKycName,
    currentKycLevel,
    lastLoginInfo,
    setLastLoginInfo,
    loading,
    isBindEmail,
    isBindGoogle,
    isBindMobile,
    isSetTradePassword,
    refreshUserInfo,
    logoutAndToHome,
    isOpenSecurity,
    getCoinPermissionByItemId,
    clearToken,
    setUserInfo,
    currentSafetyLevel,
    currentSafetyLevelLabel,
    currentNickname,
    isOpenPassKey,
    currentArea,
    getToken,
    currentVipLevel,
    // KYC 相关
    kycInfo,
    kycLevels,
    seniorThirdType,
    isKycLoading,
    fetchKycData,
  }
})
